/**
 * src/components/spatial/Tiles3DLoaderPanel.tsx
 *
 * Small, self-contained control that lets an operator stream an OGC
 * **3D Tiles** tileset (photogrammetry / BIM / point cloud) onto the
 * existing Mapbox map and have it sit in register with GeoTrust's own
 * spatial-object extrusions.
 *
 * It owns a `Tiles3DOverlayManager` (deck.gl MapboxOverlay + Tile3DLayer)
 * tied to the host map's lifetime, and exposes:
 *   - a `tileset.json` URL field (with a couple of public examples),
 *   - an optional Cesium Ion asset id + token,
 *   - an opacity slider,
 *   - Load / Clear buttons,
 *   - a "fly to tileset" jump once the root is parsed.
 *
 * No new infra: all tile streaming/culling happens client-side from the
 * supplied URL; the WebGL context is shared with Mapbox.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
// NOTE: the manager statically imports deck.gl + loaders.gl (hundreds of
// KB). To keep them OUT of the main bundle we import the module lazily,
// only the first time an operator actually loads a tileset.
import type {
  Tiles3DOverlayManager,
  Tiles3DSource,
} from '../../services/spatial/tiles3dOverlay';
import { useDraggablePanel } from './useDraggablePanel';

const panelStyle: React.CSSProperties = {
  background: 'rgba(20,22,28,0.92)',
  color: '#e8eaf0',
  borderRadius: 10,
  padding: '10px 12px',
  fontSize: 12,
  border: '1px solid rgba(255,255,255,0.08)',
  boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
  width: 260,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  margin: '6px 0 2px',
  color: '#aab2c5',
  fontSize: 11,
  fontWeight: 600,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '5px 6px',
  borderRadius: 6,
  fontSize: 12,
  border: '1px solid rgba(255,255,255,0.15)',
  background: 'rgba(0,0,0,0.3)',
  color: '#e8eaf0',
};

const btnStyle: React.CSSProperties = {
  flex: 1,
  padding: '6px 8px',
  borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.15)',
  cursor: 'pointer',
  background: 'rgba(99,102,241,0.35)',
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
};

export interface Tiles3DLoaderPanelProps {
  /** The host's mapbox-gl Map instance. */
  map: unknown;
  style?: React.CSSProperties;
}

const Tiles3DLoaderPanel: React.FC<Tiles3DLoaderPanelProps> = ({ map, style }) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [ionAssetId, setIonAssetId] = useState('');
  const [ionToken, setIonToken] = useState('');
  const [opacity, setOpacity] = useState(1);
  const [status, setStatus] = useState<{ kind: 'idle' | 'loading' | 'ok' | 'error'; msg?: string }>({
    kind: 'idle',
  });
  const [loaded, setLoaded] = useState(false);

  const managerRef = useRef<Tiles3DOverlayManager | null>(null);
  const lastCenterRef = useRef<[number, number, number] | undefined>(undefined);
  const lastZoomRef = useRef<number | undefined>(undefined);
  // Default BELOW the top-right nav buttons (hamburger / settings /
  // search, ~48px tall at top:20). Draggable for anything else.
  const { containerRef, containerStyle, dragHandleProps } = useDraggablePanel({ top: 84, right: 16 });

  // Tear the manager down when the panel unmounts or the map changes.
  useEffect(() => {
    return () => {
      managerRef.current?.destroy();
      managerRef.current = null;
    };
  }, [map]);

  // Lazily construct the deck.gl-backed manager on first use so deck.gl /
  // loaders.gl stay in a separate chunk that's only fetched on demand.
  const ensureManager = async (): Promise<Tiles3DOverlayManager | null> => {
    if (managerRef.current) return managerRef.current;
    if (!map) return null;
    const { Tiles3DOverlayManager } = await import('../../services/spatial/tiles3dOverlay');
    if (managerRef.current) return managerRef.current; // race guard
    const manager = new Tiles3DOverlayManager(map, {
      onTilesetLoad: ({ cartographicCenter, zoom }) => {
        lastCenterRef.current = cartographicCenter;
        lastZoomRef.current = zoom;
        setStatus({ kind: 'ok' });
        setLoaded(true);
      },
      onError: (msg) => setStatus({ kind: 'error', msg }),
    });
    managerRef.current = manager;
    return manager;
  };

  // Keep opacity live without reloading the tileset.
  useEffect(() => {
    if (!loaded || !managerRef.current) return;
    const source = buildSource();
    if (source) managerRef.current.setTileset(source);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opacity]);

  const buildSource = (): Tiles3DSource | null => {
    const trimmedUrl = url.trim();
    const assetId = ionAssetId.trim();
    if (!trimmedUrl && !assetId) return null;
    if (assetId) {
      return {
        ionAssetId: Number(assetId),
        ionAccessToken: ionToken.trim() || undefined,
        opacity,
      };
    }
    return { url: trimmedUrl, opacity };
  };

  const handleLoad = async () => {
    const source = buildSource();
    if (!source) {
      setStatus({ kind: 'error', msg: 'Enter a tileset.json URL or a Cesium Ion asset id.' });
      return;
    }
    if (source.ionAssetId != null && Number.isNaN(source.ionAssetId)) {
      setStatus({ kind: 'error', msg: 'Cesium Ion asset id must be a number.' });
      return;
    }
    setStatus({ kind: 'loading' });
    setLoaded(false);
    try {
      const manager = await ensureManager();
      if (!manager) {
        setStatus({ kind: 'error', msg: 'The map is not ready yet.' });
        return;
      }
      manager.setTileset(source);
    } catch {
      setStatus({ kind: 'error', msg: 'Failed to load the 3D Tiles engine.' });
    }
  };

  const handleClear = () => {
    managerRef.current?.clear();
    setLoaded(false);
    setStatus({ kind: 'idle' });
  };

  const handleFlyTo = () => {
    const center = lastCenterRef.current;
    if (!center || !map) return;
    const m = map as { flyTo?: (o: Record<string, unknown>) => void };
    try {
      m.flyTo?.({
        center: [center[0], center[1]],
        zoom: lastZoomRef.current ?? 16,
        pitch: 60,
        duration: 1500,
      });
    } catch { /* ignore */ }
  };

  const statusLine = useMemo(() => {
    switch (status.kind) {
      case 'loading': return <span style={{ color: '#fbbf24' }}>Streaming tileset…</span>;
      case 'ok': return <span style={{ color: '#4ade80' }}>Tileset loaded.</span>;
      case 'error': return <span style={{ color: '#f87171' }}>{status.msg || 'Failed to load.'}</span>;
      default: return <span style={{ color: '#8890a4' }}>Idle.</span>;
    }
  }, [status]);

  if (!open) {
    return (
      <div
        ref={containerRef}
        style={{ ...containerStyle, ...panelStyle, width: 'auto', padding: 0, display: 'flex', alignItems: 'stretch', overflow: 'hidden', ...style }}
      >
        <span
          {...dragHandleProps}
          title="Drag to move"
          style={{ ...dragHandleProps.style, display: 'flex', alignItems: 'center', padding: '0 6px', color: '#8890a4', borderRight: '1px solid rgba(255,255,255,0.08)' }}
        >⠿</span>
        <button
          data-no-drag
          onClick={() => setOpen(true)}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px 12px', fontWeight: 700, color: '#e8eaf0', fontSize: 12 }}
          title="Stream an OGC 3D Tiles tileset"
        >
          🏔 3D Tiles
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ ...containerStyle, ...panelStyle, ...style }}>
      <div {...dragHandleProps} style={{ ...dragHandleProps.style, display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ color: '#8890a4', marginRight: 6 }}>⠿</span>
        <strong style={{ flex: 1, fontSize: 12, letterSpacing: 0.3 }}>3D Tiles stream</strong>
        <button
          data-no-drag
          onClick={() => setOpen(false)}
          style={{ border: 'none', background: 'transparent', color: '#aab2c5', cursor: 'pointer', fontSize: 14 }}
          title="Close"
        >✕</button>
      </div>

      <label style={labelStyle}>tileset.json URL</label>
      <input
        style={inputStyle}
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://…/tileset.json"
        spellCheck={false}
      />

      <label style={labelStyle}>…or Cesium Ion asset id</label>
      <input
        style={inputStyle}
        value={ionAssetId}
        onChange={(e) => setIonAssetId(e.target.value)}
        placeholder="e.g. 96188"
        inputMode="numeric"
      />
      {ionAssetId.trim() !== '' && (
        <>
          <label style={labelStyle}>Cesium Ion access token</label>
          <input
            style={inputStyle}
            value={ionToken}
            onChange={(e) => setIonToken(e.target.value)}
            placeholder="ion access token"
            spellCheck={false}
          />
        </>
      )}

      <label style={labelStyle}>Opacity: {opacity.toFixed(2)}</label>
      <input
        type="range"
        min={0.1}
        max={1}
        step={0.05}
        value={opacity}
        onChange={(e) => setOpacity(Number(e.target.value))}
        style={{ width: '100%' }}
      />

      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <button onClick={handleLoad} style={btnStyle}>Load</button>
        <button
          onClick={handleClear}
          style={{ ...btnStyle, background: 'rgba(255,255,255,0.06)' }}
        >Clear</button>
      </div>
      {loaded && (
        <button
          onClick={handleFlyTo}
          style={{ ...btnStyle, background: 'rgba(255,255,255,0.06)', width: '100%', marginTop: 6 }}
        >Fly to tileset</button>
      )}

      <div style={{ marginTop: 8, fontSize: 11 }}>{statusLine}</div>
    </div>
  );
};

export default Tiles3DLoaderPanel;
